import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#startWhenNear();
  }

  updatedCallback() {
    super.updatedCallback();

    this.#startWhenNear();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * With `data-lazy` (Theme settings → Media loading → "Lazy-load media below the first section"),
   * waits until the video is within a screen's height of the viewport before giving it its sources,
   * so nothing downloads for a background further down the page. One already in view starts at once.
   */
  #startWhenNear() {
    this.#observer?.disconnect();
    this.#observer = null;

    if (!this.hasAttribute('data-lazy')) {
      this.updateVideoPlayback();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#observer?.disconnect();
        this.#observer = null;
        this.updateVideoPlayback();
      },
      { rootMargin: '100% 0px' }
    );
    this.#observer.observe(this);
  }

  /**
   * Updates source URLs after section re-renders, then reloads and starts playback.
   */
  updateVideoPlayback() {
    const { videoSources, videoElement } = this.refs;

    videoElement.muted = true;
    videoElement.playsInline = true;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    const playPromise = videoElement.play();

    if (playPromise instanceof Promise) {
      void playPromise.catch(() => null);
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
